import React, { useContext, useState } from 'react';
import { toast } from 'react-toastify';
import { BookingOrigin, PriceStatus, UnitType } from '@utriper/core';
import { UnitsService } from 'core/api/services/units';
import { PriceGroupTabService } from 'core/api/services/priceGroupTab';
import { parseUnits, parseUnitsValues } from 'core/api/parser/units';
import { PriceGroupService } from 'core/api/services/priceGroup';
import { AttachMoney, FormatListNumbered, InfoOutlined, People, Settings, UploadFile } from '@mui/icons-material';
import { parsePriceGroup } from 'core/api/parser/priceGroup';
import { useTranslation } from 'react-i18next';

const PriceGroupContext = React.createContext(null);

// TODO: Split the provider in two: one for the wizard and one for the data
export const PriceGroupProvider = ({ children }) => {
    const { t, i18n } = useTranslation();
    const translate = (key, options) => t(`price_group_context.${key}`, options);
    const initialSteps = [
        { id: 1, label: translate('initial_steps.step_1'), icon: InfoOutlined },
        { id: 2, label: translate('initial_steps.step_2'), icon: People },
        { id: 3, label: translate('initial_steps.step_3'), icon: Settings },
        { id: 4, label: translate('initial_steps.step_4'), icon: FormatListNumbered },
        { id: 5, label: translate('initial_steps.step_5'), icon: AttachMoney },
    ];
    const initialPriceGroupData = {
        name: '',
        description: '',
        status: PriceStatus.IDS.live,
        experienceId: null,
        bookingOrigins: [],
    };
    const initialUnitsForm = {
        unitsIds: [],
    };
    const initialQuantityForm = {
        ranges: [{ quantityFrom: 1, quantityTo: '' }],
    };
    const initialPriceForm = {
        priceType: null,
        currency: null,
        priceRanges: [{ quantityFrom: 1, quantityTo: '', prices: [{ unitId: null, price: null, bookingOriginId: null }] }],
    };
    // TODO: MOVE CONFIG TYPES TO CORE LIBRARY
    const CONFIG_TYPES = {
        MANUAL: 1,
        EXCEL: 2,
    };

    // Data States
    const [units, setUnits] = useState([]);
    const [priceGroupTabId, setPriceGroupTabId] = useState(0);
    const [newPriceGroupId, setNewPriceGroupId] = useState(0);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Stepper States
    const [priceGroupData, setPriceGroupData] = useState({ ...initialPriceGroupData });
    const [unitsOptions, setUnitsOptions] = useState([]);
    const [unitsForm, setUnitsForm] = useState({ ...initialUnitsForm });
    const [configType, setConfigType] = useState(0);
    const [quantityForm, setQuantityForm] = useState({ ...initialQuantityForm });
    const [priceForm, setPriceForm] = useState({ ...initialPriceForm });
    const [activeStep, setActiveStep] = useState(0);
    const [stepsWithIcons, setStepsWithIcons] = useState(initialSteps);
    const [isSuccess, setSuccess] = useState(false);

    // Price Group Data
    const [loadingPriceGroup, setLoadingPriceGroup] = useState(false);
    const [loadingPrices, setLoadingPrices] = useState(false);
    const [submittingPriceGroup, setSubmittingPriceGroup] = useState(false);
    const [priceGroup, setPriceGroup] = useState(null);
    const [prices, setPrices] = useState([]);
    const [bookingOrigins, setBookingOrigins] = useState([]);
    const [unitsPriceGroup, setUnitsPriceGroup] = useState([]);

    // Stepper Functions
    const handleNext = () => {
        setActiveStep((prevActiveStep) => Math.min(prevActiveStep + 1, stepsWithIcons.length - 1));
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => Math.max(prevActiveStep - 1, 0));
    };

    const resetWizard = () => {
        setPriceGroupData({ ...initialPriceGroupData });
        setUnitsForm({ ...initialUnitsForm });
        setConfigType(0);
        setQuantityForm({ ...initialQuantityForm });
        setPriceForm({ ...initialPriceForm });
        setActiveStep(0);
        setStepsWithIcons(initialSteps);
    };

    const handleUpdatePriceGroupData = (newValues) => {
        setPriceGroupData({ ...newValues });
    };

    const handleUpdateUnits = (newValues) => {
        setUnitsForm({ ...newValues });
    };

    const handleSettings = (newConfigType) => {
        setConfigType(newConfigType);
        setStepsWithIcons((prevValues) =>
            prevValues.map((step) => {
                if (step.id === 4) {
                    let newLabel = step.label;
                    let newIcon = step.icon;
                    if (newConfigType === CONFIG_TYPES.MANUAL) {
                        newLabel = translate('step_4.ranges');
                    } else if (newConfigType === CONFIG_TYPES.EXCEL) {
                        newLabel = translate('step_4.excel');
                        newIcon = UploadFile;
                    }
                    return { ...step, label: newLabel, icon: newIcon };
                }
                return step;
            })
        );
    };

    const handleUpdateQuantity = (ranges = []) => {
        setQuantityForm({ ranges: [...ranges] });

        const priceRanges = [];
        for (const { quantityFrom, quantityTo } of ranges) {
            let newPrices = [];

            for (const { unitId } of unitsForm?.unitsIds ?? []) {
                if (!Number.isInteger(unitId)) continue;

                newPrices.push({
                    unitId,
                    price: null,
                    bookingOriginId: null,
                });

                for (const bookingOrigin of priceGroupData?.bookingOrigins ?? []) {
                    if (!Number.isInteger(bookingOrigin?.id)) continue;

                    newPrices.push({
                        unitId,
                        price: null,
                        bookingOriginId: bookingOrigin.id,
                    });
                }
            }

            priceRanges.push({ quantityFrom, quantityTo, prices: newPrices });
        }

        setPriceForm((prevValues) => ({ ...prevValues, priceRanges }));
    };

    const validateRanges = (ranges = []) => {
        if (!Array.isArray(ranges)) return { ranges: translate('validate_ranges.required') };

        const errors = [];

        for (let i = 0; i < ranges.length; i++) {
            const r = ranges[i] || {};
            const e = {};

            const fromRaw = r.quantityFrom;
            const toRaw = r.quantityTo;

            const from = fromRaw === '' || fromRaw === undefined ? undefined : Number(fromRaw);
            const to = toRaw === '' || toRaw === undefined ? undefined : Number(toRaw);

            // Basic required and positive checks
            if (from === undefined || Number.isNaN(from)) {
                e.quantityFrom = translate('validate_ranges.required');
            } else if (from <= 0 || !Number.isInteger(from)) {
                e.quantityFrom = translate('validate_ranges.minor_quantity_from');
            }

            if (to !== undefined && Number.isNaN(to)) {
                e.quantityTo = translate('validate_ranges.invalid_quantity_to');
            } else if (to !== undefined && (!Number.isInteger(to) || to <= 0)) {
                e.quantityTo = translate('validate_ranges.minor_quantity_to');
            }

            // First must start at 1
            if (i === 0 && from !== undefined && from !== 1) {
                e.quantityFrom = translate('validate_ranges.quantity_from_start');
            }

            // If to exists, must be >= from
            if (to !== undefined && from !== undefined && to < from) {
                e.quantityTo = translate('validate_ranges.quantity_to_minor_than_from');
            }

            // No overlapping with previous
            if (i > 0) {
                const prev = ranges[i - 1] || {};
                const prevTo = prev.quantityTo === '' || prev.quantityTo === undefined ? undefined : Number(prev.quantityTo);
                // const prevFrom = prev.quantityFrom === '' || prev.quantityFrom === undefined ? undefined : Number(prev.quantityFrom);

                if (Number.isInteger(prevTo) && from - 1 !== prevTo) {
                    e.quantityFrom = translate('validate_ranges.gap_ranges');
                }

                // If previous has empty "to", it's infinite -> cannot have a next range
                if (prevTo === undefined) {
                    e.quantityFrom = translate('validate_ranges.invalid_empty');
                } else if (from !== undefined && prevTo !== undefined && from <= prevTo) {
                    e.quantityFrom = `${translate('validate_ranges.more_than')} ${prevTo}`;
                }
            }

            errors[i] = Object.keys(e).length ? e : undefined;
        }

        // Only last can have empty quantityTo
        for (let i = 0; i < ranges.length; i++) {
            const r = ranges[i] || {};
            const toRaw = r.quantityTo;
            const to = toRaw === '' || toRaw === undefined ? undefined : Number(toRaw);
            if (to === undefined && i !== ranges.length - 1) {
                errors[i] = errors[i] || {};
                errors[i].quantityTo = translate('validate_ranges.invalid_infinity');
            }
        }

        // Remove undefined entries
        const hasAny = errors.some((x) => x !== undefined);
        return hasAny ? { ranges: errors } : undefined;
    };

    const validatePrices = (ranges = [], units = []) => {
        let errors = [];
        if (ranges?.length === 0) {
            errors.push(translate('validate_prices.no_prices'));
            return errors;
        }

        for (let i = 0; i < ranges.length; i++) {
            const { quantityFrom, quantityTo, prices = [] } = ranges[i];

            let defaultError = quantityTo ? `${quantityFrom} ${translate('validate_prices.to')} ${quantityTo}` : `${quantityFrom}+`;

            if (prices?.length === 0) {
                errors.push(`${translate('validate_prices.no_price_range')} ${defaultError}`);
            }

            for (let j = 0; j < prices.length; j++) {
                const { price, unitId, bookingOriginId } = prices[j];

                let origin = translate('validate_prices.default_origin');
                if (!Number.isInteger(unitId)) {
                    continue;
                }

                if (bookingOriginId) {
                    origin = BookingOrigin.getTranslatedName(BookingOrigin.types, bookingOriginId, i18n.language);
                }

                let { name: unitName } = units?.find((unit) => unit?.id === unitId);

                if (!Number.isFinite(price)) {
                    errors.push(
                        translate('validate_prices.invalid_price', {
                            unitName,
                            origin,
                            quantityFrom,
                            quantityTo,
                        })
                    );
                }

                if (price <= 0) {
                    errors.push(
                        translate('validate_prices.minor_than_zero', {
                            unitName,
                            origin,
                            quantityFrom,
                            quantityTo,
                        })
                    );
                }
            }
        }

        return errors;
    };

    const getNewUnitTypes = (language = 'es') => {
        let unitsTypeOptions = [];

        if (units?.length === 0) {
            return [];
        }

        for (let i = 0; i < units.length; i++) {
            const { typeId } = units[i];

            const isAdded = unitsOptions.some((unit) => unit?.typeId === typeId);

            if (!isAdded) {
                const unitOption = {
                    id: typeId,
                    name: UnitType.getTranslatedName(UnitType.types, typeId, language),
                };
                unitsTypeOptions.push(unitOption);
            }
        }

        return unitsTypeOptions;
    };

    const addNewUnitOption = (typeId, handleClose = () => {}) => {
        try {
            if (!units?.length) {
                throw new Error(translate('add_new_unit_option.units_length'));
            }
            if (unitsOptions?.length === units?.length) {
                throw new Error(translate('add_new_unit_option.units_length'));
            }

            const isDuplicated = unitsOptions.some((unit) => unit?.typeId === typeId);

            if (isDuplicated) {
                throw new Error(translate('add_new_unit_option.unit_duplicated'));
            }

            const unitToAdd = units.find((unit) => unit?.typeId === typeId);

            if (!Number.isInteger(unitToAdd?.typeId)) {
                throw new Error(translate('add_new_unit_option.unit_not_found'));
            }

            setUnitsOptions((prevUnits) => [...prevUnits, unitToAdd]);

            return true;
        } catch (error) {
            toast.error(error?.message, {
                theme: 'colored',
                position: 'top-right',
            });
            return false;
        } finally {
            handleClose();
        }
    };

    // Data Functions
    const getInitialValues = async (priceGroupTabId, history) => {
        try {
            setLoading(true);
            if (!priceGroupTabId) {
                throw new Error(translate('invalid_price_group_tab'));
            }
            const { priceGroupTab } = await PriceGroupTabService.getById(priceGroupTabId);

            if (!priceGroupTab?.id) {
                throw new Error(translate('invalid_price_group_tab'));
            }

            const result = await UnitsService.list();

            if (!Array?.isArray(result)) {
                throw new Error(translate('get_initial_values.error'));
            }

            if (result?.length === 0) {
                throw new Error(translate('get_initial_values.no_units'));
            }

            setPriceGroupTabId(priceGroupTab.id);
            const unitsParsed = parseUnits(result);
            setUnits(unitsParsed);
            setUnitsOptions(unitsParsed?.slice(0, 3));

            return true;
        } catch (error) {
            setPriceGroupTabId(0);
            setUnits([]);
            setUnitsOptions([]);
            toast.error(error?.message, {
                theme: 'colored',
                position: 'top-right',
            });
            history?.push('/price-list');

            return false;
        } finally {
            setLoading(false);
        }
    };

    const addPriceGroup = async () => {
        try {
            setSubmitting(true);

            if (!priceGroupTabId) {
                throw new Error(translate('invalid_price_group_tab'));
            }

            if (!priceGroupData?.name) {
                throw new Error(translate('add_price_group.name_is_required'));
            }

            if (!priceForm?.currency?.label || !Number.isInteger(priceForm?.priceType) || !Array.isArray(priceForm?.priceRanges) || priceForm?.priceRanges?.length === 0) {
                throw new Error(translate('add_price_group.invalid_values'));
            }

            const result = await PriceGroupService.create({ priceGroupTabId, priceGroupData, priceData: priceForm });

            if (!result?.id) {
                throw new Error(translate('add_price_group.error'));
            }

            resetWizard();
            setNewPriceGroupId(result?.id);

            return true;
        } catch (error) {
            toast.error(error?.message, {
                theme: 'colored',
                position: 'top-right',
            });

            return false;
        } finally {
            setSubmitting(false);
        }
    };

    // Price Group Data
    const getPriceGroupById = async (priceGroupId, history) => {
        try {
            setLoadingPriceGroup(true);

            const { priceGroup, prices } = await PriceGroupService.getById(priceGroupId);

            if (!priceGroup?.id) {
                throw new Error(translate('price_group_not_found'));
            }

            const { priceGroupData, priceRanges, units, bookingOrigins } = parsePriceGroup(priceGroup, prices);

            setPriceGroup(priceGroupData);
            setBookingOrigins(bookingOrigins);
            setPrices(priceRanges);
            setUnitsPriceGroup(units);

            return true;
        } catch (error) {
            toast.error(error?.message, {
                theme: 'colored',
                position: 'top-right',
            });
            history?.push('/price-list');

            return false;
        } finally {
            setLoadingPriceGroup(false);
        }
    };

    const updatePriceGroupData = async (priceGroupId, priceGroupData) => {
        try {
            setSubmittingPriceGroup(true);

            if (!priceGroupId) return false;

            const result = await PriceGroupService.update({ priceGroupId, priceGroupData });

            if (!result) {
                throw new Error(translate('update_price_group_data.error'));
            }

            toast.success(translate('update_price_group_data.success'), {
                theme: 'colored',
                position: 'top-right',
            });

            return true;
        } catch (error) {
            toast.error(error?.message, {
                theme: 'colored',
                position: 'top-right',
            });

            return false;
        } finally {
            setSubmittingPriceGroup(false);
            getPriceGroupById(priceGroupId);
        }
    };

    const handleUpdatePrice = async (priceGroupId, priceData) => {
        try {
            setLoadingPrices(true);

            const result = await PriceGroupService.update({ priceGroupId, priceData });

            if (!result) {
                throw new Error(translate('handle_update_price'));
            }

            return true;
        } catch (error) {
            toast.error(error?.message, {
                theme: 'colored',
                position: 'top-right',
            });
            getPriceGroupById(priceGroupId);

            return false;
        } finally {
            setLoadingPrices(false);
        }
    };

    const getUnitsData = async () => {
        try {
            if (units?.some((unit) => Number.isInteger(unit?.id))) return false;

            const result = await UnitsService.list();

            if (result?.length === 0) {
                throw new Error(translate('get_units_data'));
            }

            const unitsParsed = parseUnitsValues(result);

            setUnits(unitsParsed);
        } catch (error) {
            setUnits([]);
            toast.error(error?.message, {
                theme: 'colored',
                position: 'top-right',
            });

            return false;
        }
    };

    const addUnit = async (priceGroupId, unitId, priceRanges) => {
        try {
            setSubmittingPriceGroup(true);

            if (!Number.isInteger(priceGroupId)) {
                throw new Error(translate('invalid_price_group'));
            }

            if (!Number.isInteger(unitId)) {
                throw new Error(translate('add_unit.unit_id'));
            }

            if (priceRanges?.length === 0) {
                throw new Error(translate('add_unit.ranges'));
            }

            const result = await PriceGroupService.addUnit({ priceGroupId, unitId, priceRanges });

            toast.success(translate('add_unit.success'), {
                theme: 'colored',
                position: 'top-right',
            });

            return result;
        } catch (error) {
            toast.error(error?.message, {
                theme: 'colored',
                position: 'top-right',
            });

            return false;
        } finally {
            setSubmittingPriceGroup(false);
            getPriceGroupById(priceGroupId);
        }
    };

    const addRange = async (priceGroupId, priceRanges) => {
        try {
            setSubmittingPriceGroup(true);

            if (!Number.isInteger(priceGroupId)) {
                throw new Error(translate('invalid_price_group'));
            }

            if (priceRanges?.length === 0) {
                throw new Error(translate('add_range.prices'));
            }

            const result = await PriceGroupService.addRange({ priceGroupId, priceRanges });

            toast.success(translate('add_range.success'), {
                theme: 'colored',
                position: 'top-right',
            });

            return result;
        } catch (error) {
            toast.error(error?.message, {
                theme: 'colored',
                position: 'top-right',
            });

            return false;
        } finally {
            setSubmittingPriceGroup(false);
            getPriceGroupById(priceGroupId);
        }
    };

    const addBookingOrigin = async (priceGroupId, bookingOriginId, priceRanges) => {
        try {
            setSubmittingPriceGroup(true);

            if (!Number.isInteger(priceGroupId)) {
                throw new Error(translate('invalid_price_group'));
            }

            if (!Number.isInteger(bookingOriginId)) {
                throw new Error(translate('add_booking_origin.missing_origin_id'));
            }

            if (!BookingOrigin.isValidId(bookingOriginId)) {
                throw new Error(translate('add_booking_origin.invalid_origin_id'));
            }

            if (priceRanges?.length === 0) {
                throw new Error(translate('add_booking_origin.prices'));
            }

            const result = await PriceGroupService.addBookingOrigin({ priceGroupId, bookingOriginId, priceRanges });

            toast.success(translate('add_booking_origin.success'), {
                theme: 'colored',
                position: 'top-right',
            });

            return result;
        } catch (error) {
            toast.error(error?.message, {
                theme: 'colored',
                position: 'top-right',
            });

            return false;
        } finally {
            setSubmittingPriceGroup(false);
            getPriceGroupById(priceGroupId);
        }
    };

    const editUnit = async (priceGroupId, oldUnitId, newUnitId) => {
        try {
            setSubmittingPriceGroup(true);

            if (!Number.isFinite(priceGroupId)) {
                throw new Error(translate('invalid_price_group'));
            }

            if (!Number.isFinite(oldUnitId) || !Number.isFinite(newUnitId)) {
                throw new Error(translate('edit_unit.unit_type'));
            }

            const result = await PriceGroupService.editUnit({ priceGroupId, oldUnitId, newUnitId });

            toast.success(translate('edit_unit.success'), {
                theme: 'colored',
                position: 'top-right',
            });

            return result;
        } catch (error) {
            toast.error(error?.message, {
                theme: 'colored',
                position: 'top-right',
            });

            return false;
        } finally {
            setSubmittingPriceGroup(false);
            getPriceGroupById(priceGroupId);
        }
    };

    const editRanges = async (priceGroupId, oldRanges, newRanges) => {
        try {
            setSubmittingPriceGroup(true);

            if (!Number.isFinite(priceGroupId)) {
                throw new Error(translate('invalid_price_group'));
            }

            if (!Array.isArray(oldRanges) || oldRanges?.length === 0 || !Array.isArray(newRanges) || newRanges?.length === 0) {
                throw new Error(translate('edit_ranges.ranges'));
            }

            const result = await PriceGroupService.editRanges({ priceGroupId, oldRanges, newRanges });

            toast.success(translate('edit_ranges.success'), {
                theme: 'colored',
                position: 'top-right',
            });

            return result;
        } catch (error) {
            toast.error(error?.message, {
                theme: 'colored',
                position: 'top-right',
            });

            return false;
        } finally {
            setSubmittingPriceGroup(false);
            getPriceGroupById(priceGroupId);
        }
    };

    const deleteUnit = async (priceGroupId, unitId, unitName) => {
        try {
            setSubmittingPriceGroup(true);
            if (!Number.isFinite(priceGroupId)) {
                throw new Error(translate('invalid_price_group'));
            }

            if (!Number.isFinite(unitId)) {
                throw new Error(translate('delete_unit.invalid_unit'));
            }

            const result = await PriceGroupService.deleteUnit({ priceGroupId, unitId });

            toast.success(`${translate('delete_unit.success_1')} ${unitName} ${translate('delete_unit.success_2')}`, {
                theme: 'colored',
                position: 'top-right',
            });

            return result;
        } catch (error) {
            toast.error(error?.message, {
                theme: 'colored',
                position: 'top-right',
            });

            return false;
        } finally {
            setSubmittingPriceGroup(false);
            getPriceGroupById(priceGroupId);
        }
    };

    const deleteRange = async (priceGroupId, quantityFrom, quantityTo) => {
        try {
            setSubmittingPriceGroup(true);

            if (!Number.isFinite(priceGroupId)) {
                throw new Error(translate('invalid_price_group'));
            }

            if (!Number.isFinite(quantityFrom) || !Number.isFinite(quantityTo)) {
                throw new Error(translate('delete_range.invalid_range'));
            }

            const result = await PriceGroupService.deleteRange({ priceGroupId, quantityFrom, quantityTo });

            toast.success(`${translate('delete_range.success_1')} ${quantityFrom} ${translate('delete_range.to')} ${quantityTo} ${translate('delete_range.success_2')}`, {
                theme: 'colored',
                position: 'top-right',
            });

            return result;
        } catch (error) {
            toast.error(error?.message, {
                theme: 'colored',
                position: 'top-right',
            });

            return false;
        } finally {
            setSubmittingPriceGroup(false);
            getPriceGroupById(priceGroupId);
        }
    };

    const deleteBookingOrigin = async (priceGroupId, bookingOriginId) => {
        try {
            setSubmittingPriceGroup(true);
            if (!Number.isFinite(priceGroupId)) {
                throw new Error(translate('invalid_price_group'));
            }

            if (!Number.isInteger(bookingOriginId)) {
                throw new Error(translate('delete_booking_origin.missing_origin_id'));
            }

            if (!BookingOrigin.isValidId(bookingOriginId)) {
                throw new Error(translate('delete_booking_origin.invalid_origin_id'));
            }

            const result = await PriceGroupService.deleteBookingOrigin({ priceGroupId, bookingOriginId });

            toast.success(translate('delete_booking_origin.success', { origin: BookingOrigin.getTranslatedName(BookingOrigin.types, bookingOriginId, i18n.language)?.[0] }), {
                theme: 'colored',
                position: 'top-right',
            });

            return result;
        } catch (error) {
            toast.error(error?.message, {
                theme: 'colored',
                position: 'top-right',
            });

            return false;
        } finally {
            setSubmittingPriceGroup(false);
            getPriceGroupById(priceGroupId);
        }
    };

    const movePriceGroup = async (priceGroupId, priceGroupTabId) => {
        try {
            setSubmittingPriceGroup(true);

            const result = await PriceGroupService.movePriceGroup({ priceGroupId, priceGroupTabId });

            toast.success(translate('move_price_group.success'), {
                theme: 'colored',
                position: 'top-right',
            });

            getPriceGroupById(priceGroupId);

            return result;
        } catch (error) {
            toast.error(error?.message, {
                theme: 'colored',
                position: 'top-right',
            });

            return false;
        } finally {
            setSubmittingPriceGroup(false);
        }
    };

    return (
        <PriceGroupContext.Provider
            value={{
                // Data
                units,
                priceGroupTabId,
                newPriceGroupId,
                setNewPriceGroupId,
                loading,
                isSuccess,
                getInitialValues,
                // Step 1
                priceGroupData,
                handleUpdatePriceGroupData,
                // Step 2
                unitsOptions,
                unitsForm,
                handleUpdateUnits,
                getNewUnitTypes,
                addNewUnitOption,
                // Step 3
                configType,
                handleSettings,
                // Step 4
                validateRanges,
                quantityForm,
                handleUpdateQuantity,
                // Step 5
                validatePrices,
                priceForm,
                setPriceForm,
                addPriceGroup,
                setSuccess,
                submitting,
                // Stepper
                CONFIG_TYPES,
                activeStep,
                stepsWithIcons,
                handleNext,
                handleBack,
                resetWizard,

                // Price Group By Id
                getUnitsData,
                getPriceGroupById,
                updatePriceGroupData,
                handleUpdatePrice,
                addUnit,
                addRange,
                addBookingOrigin,
                editUnit,
                editRanges,
                deleteUnit,
                deleteRange,
                deleteBookingOrigin,
                movePriceGroup,
                loadingPriceGroup,
                loadingPrices,
                submittingPriceGroup,
                priceGroup,
                prices,
                setPrices,
                bookingOrigins,
                unitsPriceGroup,
            }}
        >
            {children}
        </PriceGroupContext.Provider>
    );
};

PriceGroupProvider.displayName = 'PriceGroupProvider';

export const usePriceGroup = () => useContext(PriceGroupContext);
